import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "../brand/Logo";

const NAV_ITEMS = [
  { label: "What I Build", to: "/#two-ways" },
  { label: "My Work", to: "/projects" },
  { label: "AI Front Desk", to: "/front-office" },
  { label: "Coaching", to: "/pricing" },
  { label: "About", to: "/#founder" },
];

export const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <>
      <header
        data-testid="nav-root"
        className="fixed top-0 inset-x-0 z-50 transition-colors"
        style={{
          background: scrolled ? "rgba(11,30,58,0.9)" : "transparent",
          backdropFilter: scrolled ? "blur(14px) saturate(140%)" : "none",
          WebkitBackdropFilter: scrolled ? "blur(14px) saturate(140%)" : "none",
          borderBottom: scrolled ? "1px solid var(--ggm-border-subtle)" : "1px solid transparent",
          transitionDuration: "var(--ggm-duration-ui)",
        }}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-[84px] md:h-[92px] flex items-center justify-between gap-4 md:gap-6">
          <Link
            to="/"
            aria-label="Gadget Guy Media home"
            data-testid="nav-logo-link"
            className="shrink-0 focus:outline-none focus:ring-2 focus:ring-[var(--ggm-signal-cyan)] rounded"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            {/* Larger header logo — 52px mobile / 60px desktop */}
            <Logo variant="horizontal" height={52} className="md:!h-[60px]" />
          </Link>

          <nav className="hidden lg:flex items-center gap-8" data-testid="nav-desktop">
            {NAV_ITEMS.map((item) => {
              const isHash = item.to.startsWith("/#");
              const cls = "font-condensed uppercase tracking-widest text-[13px] text-white/70 hover:text-white transition-colors";
              const testId = `nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`;
              return isHash ? (
                <a
                  key={item.label}
                  href={item.to}
                  className={cls}
                  style={{ transitionDuration: "var(--ggm-duration-ui)" }}
                  data-testid={testId}
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.label}
                  to={item.to}
                  className={cls}
                  style={{ transitionDuration: "var(--ggm-duration-ui)" }}
                  data-testid={testId}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            {/* Desktop CTA — restrained mass; brand → nav → CTA hierarchy */}
            <a
              href="/audit"
              className="ggm-btn-primary !hidden sm:!inline-flex"
              style={{ padding: "11px 22px", fontSize: 14 }}
              data-testid="nav-cta-start-project"
            >
              Free AI Audit <ArrowUpRight size={15} />
            </a>
            {/* Mobile compact CTA — 44px tap target, restrained mass */}
            <a
              href="/audit"
              className="ggm-btn-primary !inline-flex sm:!hidden whitespace-nowrap"
              style={{
                padding: "10px 14px",
                fontSize: 12,
                letterSpacing: "0.03em",
                gap: 6,
                minHeight: 44,
              }}
              data-testid="nav-cta-start-project-mobile"
            >
              Free Audit <ArrowUpRight size={13} />
            </a>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-md border border-white/10 text-white"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              data-testid="nav-menu-toggle"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="nav-mobile-drawer"
          className="fixed inset-0 z-40 lg:hidden"
          style={{ background: "rgba(6,12,26,0.88)", backdropFilter: "blur(6px)" }}
          onClick={() => setOpen(false)}
        >
          <div
            className="absolute right-0 top-0 h-full w-[86%] max-w-sm p-6 pt-24 flex flex-col gap-2"
            style={{
              background: "var(--ggm-surface)",
              borderLeft: "1px solid var(--ggm-border-subtle)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {NAV_ITEMS.map((item) => {
              const isHash = item.to.startsWith("/#");
              const cls = "font-condensed uppercase tracking-widest text-lg text-white/85 hover:text-[var(--ggm-signal-cyan)] py-3 px-2 rounded-md";
              const testId = `nav-mobile-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`;
              return isHash ? (
                <a
                  key={item.label}
                  href={item.to}
                  onClick={() => setOpen(false)}
                  className={cls}
                  style={{ transition: "color var(--ggm-duration-ui) ease" }}
                  data-testid={testId}
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={cls}
                  style={{ transition: "color var(--ggm-duration-ui) ease" }}
                  data-testid={testId}
                >
                  {item.label}
                </Link>
              );
            })}
            <a
              href="/audit"
              onClick={() => setOpen(false)}
              className="ggm-btn-primary mt-6"
              data-testid="nav-mobile-cta-start-project"
            >
              Free AI Audit <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;
